import React, { useState } from 'react';
import {
  Button,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Typography
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';

interface ModelOption {
  name: string;
  value: string;
}

interface ModelSelectorButtonProps {
  selectedModel: string;
  onModelSelect: (modelValue: string) => void;
  disabled?: boolean;
  models?: ModelOption[];
}

const defaultModels: ModelOption[] = [
  { name: "Default AI Model", value: "" },
  { name: "Gemini 2.5 Pro", value: "gemini-2.5-pro" },
  { name: "GPT 5.1", value: "gpt-5.1" },
  { name: "Claude Sonnet 4.5", value: "claude-sonnet-4-5" }
];

const ModelSelectorButton: React.FC<ModelSelectorButtonProps> = ({
  selectedModel,
  onModelSelect,
  disabled = false,
  models = defaultModels
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (modelValue: string) => {
    onModelSelect(modelValue);
    handleClose();
  };

  const popoverOpen = Boolean(anchorEl);
  const selectedModelName = models.find(m => m.value === selectedModel)?.name || selectedModel;

  return (
    <>
      <Button
        onClick={handleClick}
        variant="outlined"
        startIcon={<SettingsIcon />}
        disabled={disabled}
        sx={{
          borderWidth: 1,
          minWidth: 180,
          py: 1.5,
          '& .MuiButton-startIcon': {
            marginLeft: 0.5
          }
        }}
      >
        <Typography variant="body2" sx={{ textTransform: 'none', whiteSpace: 'nowrap', pr: 1 }}>
          {selectedModelName}
        </Typography>
      </Button>

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List sx={{ width: 250 }}>
          <ListItem disablePadding>
            <Box sx={{ px: 2, py: 1, width: '100%' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Select LLM Model
              </Typography>
            </Box>
          </ListItem>
          {models.map((model) => (
            <ListItem key={model.value} disablePadding>
              <ListItemButton
                selected={selectedModel === model.value}
                onClick={() => handleSelect(model.value)}
              >
                <ListItemText 
                  primary={model.name}
                  secondary={model.value}
                  primaryTypographyProps={{ fontWeight: selectedModel === model.value ? 600 : 400 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
};

export default ModelSelectorButton;

